import React from 'react'
import { Pressable, View } from 'react-native'
import SafePressable from '@ui/buttons/SafePressable'
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
} from 'react-native-reanimated'
import { twMerge } from 'tailwind-merge'
import { colors } from '@ui/colors'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import { getIconVariant, iconSizes, sButton, sText } from './styles'
import { ButtonProps } from './types'
import Loader from '@ui/elements/Loader'
import IconWithLoading from './IconWithLoading'
import { Href, Link } from 'expo-router'
import ButtonBadge from './ButtonBadge'

const Button = React.forwardRef<typeof Pressable, ButtonProps>(
  (
    {
      label,
      className,
      iconStart,
      iconEnd,
      variant = 'filled',
      size = 'md',
      width = 'content',
      disabled,
      loading,
      style,
      badge,
      staff,
      noninteractive,
      overrideColorFront,
      overrideColorBack,
      suffix,
      throttle,
      ...rest
    },
    ref
  ) => {
    const {
      animation: animationSelf,
      onPressIn,
      onPressOut,
    } = useAnimatedPress({
      duration: 300,
    })
    const animation = rest.animation || animationSelf
    const variantValue = useDerivedValue(() => variant)
    const disabledDisplay = !loading && (!!disabled || !!staff)
    const disabledStyle = useDerivedValue(() => disabledDisplay)
    const loadingStyle = useDerivedValue(() => (loading ? 1 : 0))

    const styleButton = useAnimatedStyle(() => {
      const colorsArray = rest.colors
        ? rest.colors
        : variantValue.value === 'filled'
        ? [colors.text, colors.black]
        : ['transparent', colors.text10]
      return {
        minWidth: width === 'full' ? '100%' : 'auto',
        maxWidth: width === 'full' ? '100%' : 'auto',
        transform: [{ scale: 1 - animation.value * 0.025 }],
        /* FIXME: this is not readable */
        backgroundColor:
          overrideColorBack ||
          (disabledStyle.value &&
          (!(variantValue.value === 'outlined') || size === 'xs')
            ? colors.neutral[100]
            : interpolateColor(animation.value, [0, 1], colorsArray)),
      }
    })

    const styleText = useAnimatedStyle(() => {
      return {
        opacity: interpolate(loadingStyle.value, [0, 1], [1, 0.25]),
      }
    })

    const iconVariant = getIconVariant(iconStart, iconEnd, !!label)

    const iconColor =
      overrideColorFront ??
      (disabledDisplay
        ? colors.neutral[300]
        : variant !== 'filled'
        ? colors.text
        : colors.white)

    const iconSize = iconSizes[size || 'md']

    const iconSpace = width === 'full' ? <View /> : null

    return (
      <SafePressable
        // @ts-expect-error wierd ref types
        ref={ref}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={disabled || loading || noninteractive}
        throttle={throttle}
        {...rest}
      >
        <Animated.View
          className={twMerge(
            sButton({
              variant,
              size,
              width,
              disabled: disabledDisplay,
              icon: iconVariant,
            }),
            className
          )}
          style={[styleButton, style]}
        >
          {iconStart ? (
            <IconWithLoading
              loading={!!loading}
              iconProps={{
                name: iconStart,
                color: iconColor,
                size: iconSize,
              }}
            />
          ) : (
            iconSpace
          )}
          {label && (
            <Animated.Text
              className={sText({
                variant: disabledDisplay ? 'outlined' : variant,
                size,
              })}
              style={[
                overrideColorFront ? { color: overrideColorFront } : {},
                iconVariant === 'none' && !disabled && styleText,
                disabledDisplay ? { color: colors.black25 } : {},
              ]}
            >
              {label}
              {staff && ' *'}
            </Animated.Text>
          )}

          {iconEnd ? (
            <>
              {suffix}
              <IconWithLoading
                loading={!!loading}
                iconProps={{
                  name: iconEnd,
                  color: iconColor,
                  size: iconSize,
                }}
              />
            </>
          ) : (
            suffix ?? iconSpace
          )}
          {badge && <ButtonBadge badge={badge} />}
        </Animated.View>
        {iconVariant === 'none' && (
          <View
            className="pointer-events-none absolute flex h-full w-full items-center justify-center rounded-full"
            style={{
              top: 0,
              left: 0,
              transform: [{ translateX: 0 }],
            }}
          >
            <Loader
              loading={!!loading}
              iconProps={{
                color: iconColor,
              }}
            />
          </View>
        )}
      </SafePressable>
    )
  }
)

Button.displayName = 'Button'

export const LinkOrButton = ({
  href,
  onPress,
  ...rest
}: ButtonProps & { href?: Href }) => {
  if (href)
    return (
      <Link href={href} asChild>
        <Button {...rest} onPress={!href ? onPress : undefined} />
      </Link>
    )

  return <Button {...rest} />
}

export default Button
